import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Eye, Play, Trash2, Clock, CheckCircle, XCircle, AlertCircle, StopCircle, RotateCcw, PlayCircle, Briefcase, Building2, User2, ListChecks, Edit3, X, Brain, AlertTriangle, Plus } from 'lucide-react';
import { ConfirmDialog } from '../components/shared/ConfirmDialog';
import EditNeedModal from '../components/recruiting/EditNeedModal';
import CreateWorkflowDialog from '../components/CreateWorkflowDialog';
import Toast from '../components/ui/Toast';
import { getCandidatesForNeed } from '../services/api';
import { useSofteamboardStore } from '../store/softeamboardStore';
import AppHeader from '../components/common/AppHeader';
import { useWebSocket } from '../hooks/useWebSocket';
import { validateWorkflowRequirements } from '../utils/workflowValidation';

const WorkflowRequestsList = () => {
  const {
    workflowRequests: workflowRequestsRaw,
    needs,
    getCandidatesForNeed,
    // ✅ REMOVE: loadCandidatesForNeed - we won't call API from this component
    loadNeeds,
    workflowLoading,
    workflowError,
    loadWorkflowRequests,
    triggerWorkflowRequest,
    deleteWorkflowRequest,
    cancelWorkflowRequest,
    retryWorkflowRequest
  } = useSofteamboardStore();

  // ✅ FIX: Ensure workflowRequests is always an array
  const workflowRequests = Array.isArray(workflowRequestsRaw) ? workflowRequestsRaw : [];

  const [localLoading, setLocalLoading] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // ✅ NEW: State for edit modal and validation
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedNeedForEdit, setSelectedNeedForEdit] = useState(null);

  // ✅ NEW: State for payload construction modal
  const [showPayloadModal, setShowPayloadModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // WebSocket connection for real-time updates
  const { isConnected } = useWebSocket();

  const [confirmAction, setConfirmAction] = useState({ open: false, type: null, id: null });
  const [toast, setToast] = useState(null);

  useEffect(() => {
    loadWorkflowRequests();
    setLastRefresh(Date.now());
  }, []);

  // Auto-refresh every 30 seconds if enabled
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadWorkflowRequests();
      setLastRefresh(Date.now());
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, loadWorkflowRequests]);

  const handleRefresh = () => {
    loadWorkflowRequests();
    setLastRefresh(Date.now());
  };

  // Enhanced validation function
  const validateOpportunityCreation = (needId) => {
    const need = needs.find(n => n.id === needId);
    if (!need) return ['Need not found'];
    
    const validation = validateWorkflowRequirements(need);
    return validation.missingFields
      .filter(f => f.required && f.workflow === 'opportunity')
      .map(f => f.label);
  };

  // ✅ NEW: Payload Construction Modal Component
  const PayloadConstructionModal = ({ request, isOpen, onClose, onSubmit }) => {
    const [formData, setFormData] = useState({});
    const [availableCandidates, setAvailableCandidates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
      if (isOpen && request) {
        initializeFormData();
        loadCandidatesIfNeeded();
      }
    }, [isOpen, request]);

    const initializeFormData = () => {
      const payload = request.payload || {};
      const need = needs.find(n => n.id === payload.needId);
      
      setFormData({
        needId: payload.needId || '',
        candidateId: payload.candidateId || '',
        candidateName: payload.candidateName || '',
        candidateSalesforceId: payload.candidateSalesforceId || '',
        expectedTjm: payload.expectedTjm || need?.tjm || '',
        interviewDate: payload.interviewDate || '',
        projectedStartDate: payload.projectedStartDate || '',
        endDate: payload.endDate || '',
        commercialConcession: payload.commercialConcession || 0,
        tjm: payload.tjm || need?.tjm || ''
      });
    };

    const loadCandidatesIfNeeded = async () => {
      const payload = request.payload || {};
      if (payload.needId) {
        // ✅ FIX: Validate need ID before making API call
        if (typeof payload.needId !== 'string' || payload.needId.trim() === '' || payload.needId.includes('…')) {
          console.warn('⚠️ Invalid need ID, skipping candidate load:', payload.needId);
          setAvailableCandidates([]);
          return;
        }

        try {
          const candidatesData = await getCandidatesForNeed(payload.needId);
          setAvailableCandidates(candidatesData || []);
        } catch (error) {
          console.error('Failed to load candidates:', error);
          setAvailableCandidates([]);
          
          // ✅ FIX: Show user-friendly error for missing needs
          if (error.message && error.message.includes('Need not found')) {
            setErrors(prev => ({
              ...prev,
              needId: 'Need not found - it may have been deleted'
            }));
          }
        }
      }
    };

    const validateForm = () => {
      const newErrors = {};
      
      // Common validations
      if (!formData.needId) newErrors.needId = 'Need ID is required';
      
      // Type-specific validations
      switch (request.type) {
        case 'salesforce_proposition_create':
          if (!formData.candidateName) newErrors.candidateName = 'Candidate name is required';
          if (!formData.expectedTjm || formData.expectedTjm <= 0) newErrors.expectedTjm = 'Valid TJM is required';
          break;
          
        case 'salesforce_presentation_update':
          if (!formData.candidateId) newErrors.candidateId = 'Candidate selection is required';
          if (!formData.interviewDate) newErrors.interviewDate = 'Interview date is required';
          break;
          
        case 'salesforce_retention_update':
          if (!formData.candidateId) newErrors.candidateId = 'Candidate selection is required';
          if (!formData.projectedStartDate) newErrors.projectedStartDate = 'Projected start date is required';
          break;
      }
      
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      
      if (!validateForm()) return;
      
      setLoading(true);
      try {
        // Construct the enhanced payload based on request type
        const enhancedPayload = { ...request.payload };
        
        // Add common fields
        enhancedPayload.needId = formData.needId;
        
        // Add type-specific fields
        switch (request.type) {
          case 'salesforce_proposition_create':
            enhancedPayload.candidateName = formData.candidateName;
            enhancedPayload.candidateSalesforceId = formData.candidateSalesforceId;
            enhancedPayload.expectedTjm = parseFloat(formData.expectedTjm);
            break;
            
          case 'salesforce_presentation_update':
            enhancedPayload.candidateId = formData.candidateId;
            enhancedPayload.interviewDate = formData.interviewDate;
            break;
            
          case 'salesforce_retention_update':
            enhancedPayload.candidateId = formData.candidateId;
            enhancedPayload.projectedStartDate = formData.projectedStartDate;
            enhancedPayload.endDate = formData.endDate;
            enhancedPayload.commercialConcession = parseFloat(formData.commercialConcession) || 0;
            enhancedPayload.tjm = parseFloat(formData.tjm);
            break;
        }
        
        await onSubmit(enhancedPayload);
        onClose();
      } catch (error) {
        console.error('Failed to submit payload:', error);
        setErrors({ submit: 'Failed to submit. Please try again.' });
      } finally {
        setLoading(false);
      }
    };

    if (!isOpen) return null;

    const getRequiredFields = () => {
      switch (request.type) {
        case 'salesforce_proposition_create':
          return ['candidateName', 'expectedTjm'];
        case 'salesforce_presentation_update':
          return ['candidateId', 'interviewDate'];
        case 'salesforce_retention_update':
          return ['candidateId', 'projectedStartDate'];
        default:
          return [];
      }
    };

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" role="dialog" aria-modal="true" aria-labelledby="payload-modal-title">
        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
          <div className="flex justify-between items-center mb-4">
            <h3 id="payload-modal-title" className="text-lg font-medium text-gray-900">
              Configure {request.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Candidate Selection for flows that need it */}
            {['salesforce_presentation_update', 'salesforce_retention_update'].includes(request.type) && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Candidate *
                </label>
                <select
                  value={formData.candidateId}
                  onChange={(e) => setFormData({ ...formData, candidateId: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a candidate</option>
                  {availableCandidates.map(candidate => (
                    <option key={candidate.id} value={candidate.id}>
                      {candidate.name || `Candidate ${candidate.id.substring(0, 8)}`}
                    </option>
                  ))}
                </select>
                {errors.candidateId && <p className="text-red-500 text-xs mt-1">{errors.candidateId}</p>}
              </div>
            )}

            {/* Candidate Name for proposition creation */}
            {request.type === 'salesforce_proposition_create' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Candidate Name *
                  </label>
                  <input
                    type="text"
                    value={formData.candidateName}
                    onChange={(e) => setFormData({ ...formData, candidateName: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                  {errors.candidateName && <p className="text-red-500 text-xs mt-1">{errors.candidateName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Candidate Salesforce ID
                  </label>
                  <input
                    type="text"
                    value={formData.candidateSalesforceId}
                    onChange={(e) => setFormData({ ...formData, candidateSalesforceId: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Optional - leave empty to use name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Expected TJM (€/day) *
                  </label>
                  <input
                    type="number"
                    value={formData.expectedTjm}
                    onChange={(e) => setFormData({ ...formData, expectedTjm: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    min="0"
                    required
                  />
                  {errors.expectedTjm && <p className="text-red-500 text-xs mt-1">{errors.expectedTjm}</p>}
                </div>
              </>
            )}

            {/* Interview Date for presentation flow */}
            {request.type === 'salesforce_presentation_update' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Interview Date & Time *
                </label>
                <input
                  type="datetime-local"
                  value={formData.interviewDate}
                  onChange={(e) => setFormData({ ...formData, interviewDate: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
                {errors.interviewDate && <p className="text-red-500 text-xs mt-1">{errors.interviewDate}</p>}
              </div>
            )}

            {/* Retention flow specific fields */}
            {request.type === 'salesforce_retention_update' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Projected Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.projectedStartDate}
                    onChange={(e) => setFormData({ ...formData, projectedStartDate: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                  {errors.projectedStartDate && <p className="text-red-500 text-xs mt-1">{errors.projectedStartDate}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Commercial Concession (€)
                  </label>
                  <input
                    type="number"
                    value={formData.commercialConcession}
                    onChange={(e) => setFormData({ ...formData, commercialConcession: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    TJM (€/day)
                  </label>
                  <input
                    type="number"
                    value={formData.tjm}
                    onChange={(e) => setFormData({ ...formData, tjm: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    min="0"
                  />
                </div>
              </>
            )}

            {errors.submit && (
              <div className="text-red-500 text-sm">{errors.submit}</div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin inline" />
                    Processing...
                  </>
                ) : (
                  'Start RPA Flow'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // ✅ NEW: Check if request needs payload construction
  const needsPayloadConstruction = (request) => {
    const flowTypes = [
      'salesforce_proposition_create',
      'salesforce_presentation_update', 
      'salesforce_retention_update'
    ];
    return flowTypes.includes(request.type);
  };

  const handleTrigger = async (id) => {
    const request = workflowRequests.find(r => r.id === id);
    
    // ✅ NEW: Check if this request needs payload construction
    if (needsPayloadConstruction(request)) {
      setSelectedRequest(request);
      setShowPayloadModal(true);
      return;
    }
    
    // ✅ NEW: Validate opportunity creation requirements
    if (request?.type === 'salesforce_opportunity_create' && request?.payload?.needId) {
      const missingData = validateOpportunityCreation(request.payload.needId);
      if (missingData.length > 0) {
        const need = needs.find(n => n.id === request.payload.needId);
        setSelectedNeedForEdit(need);
        setShowEditModal(true);
        return;
      }
    }
    
    setLocalLoading(prev => ({ ...prev, [`trigger-${id}`]: true }));
    try {
      await triggerWorkflowRequest(id);
      setToast({ message: 'Workflow triggered successfully', type: 'success' });
    } catch (error) {
      console.error('Failed to trigger workflow:', error);
      setToast({ message: 'Failed to trigger workflow', type: 'error' });
    } finally {
      setLocalLoading(prev => ({ ...prev, [`trigger-${id}`]: false }));
    }
  };

  // ✅ NEW: Handle payload submission
  const handlePayloadSubmit = async (enhancedPayload) => {
    if (!selectedRequest) return;
    
    setLocalLoading(prev => ({ ...prev, [`trigger-${selectedRequest.id}`]: true }));
    try {
      // ✅ FIX: Pass enhanced payload to trigger function
      await triggerWorkflowRequest(selectedRequest.id, enhancedPayload);
    } finally {
      setLocalLoading(prev => ({ ...prev, [`trigger-${selectedRequest.id}`]: false }));
    }
  };

  const handleCancel = async (id) => {
    setConfirmAction({ open: true, type: 'cancel', id });
  };

  const executeCancel = async (id) => {
    setLocalLoading(prev => ({ ...prev, [`cancel-${id}`]: true }));
    try {
      await cancelWorkflowRequest(id);
      setToast({ message: 'Workflow cancelled', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to cancel workflow', type: 'error' });
    } finally {
      setLocalLoading(prev => ({ ...prev, [`cancel-${id}`]: false }));
      setConfirmAction({ open: false, type: null, id: null });
    }
  };

  const handleRetry = async (id) => {
    setLocalLoading(prev => ({ ...prev, [`retry-${id}`]: true }));
    try {
      await retryWorkflowRequest(id);
      setToast({ message: 'Workflow retry initiated', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to retry workflow', type: 'error' });
    } finally {
      setLocalLoading(prev => ({ ...prev, [`retry-${id}`]: false }));
    }
  };

  const handleDelete = async (id) => {
    setConfirmAction({ open: true, type: 'delete', id });
  };

  const executeDelete = async (id) => {
    setLocalLoading(prev => ({ ...prev, [`delete-${id}`]: true }));
    try {
      await deleteWorkflowRequest(id);
      setToast({ message: 'Workflow deleted', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to delete workflow', type: 'error' });
    } finally {
      setLocalLoading(prev => ({ ...prev, [`delete-${id}`]: false }));
      setConfirmAction({ open: false, type: null, id: null });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'NEW':
        return <Clock className="w-4 h-4 text-gray-500" />;
      case 'PENDING':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'IN_PROGRESS':
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'COMPLETED':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'FAILED':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'CANCELLED':
        return <StopCircle className="w-4 h-4 text-gray-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    
    switch (status) {
      case 'NEW':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case 'PENDING':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'IN_PROGRESS':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'COMPLETED':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'FAILED':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'CANCELLED':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'Invalid Date';
    }
  };

  const getRelativeTime = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const seconds = Math.floor(diff / 1000);
    
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const canTrigger = (request) => {
    return ['NEW', 'PENDING', 'FAILED', 'CANCELLED'].includes(request.status);
  };

  const canCancel = (request) => {
    return ['PENDING', 'IN_PROGRESS'].includes(request.status);
  };

  const canRetry = (request) => {
    return request.status === 'FAILED';
  };

  const canDelete = (request) => {
    return !['IN_PROGRESS'].includes(request.status);
  };

  // ✅ FIX: Load candidates for needs when workflow requests are loaded
  // This useEffect is now redundant as candidates are loaded via the store's getCandidatesForNeed
  // and needs are loaded via the store's needs array.
  // We keep it for now to ensure consistency with the original logic, but it might be removed later.
  useEffect(() => {
    if (workflowRequests.length > 0) {
      // Extract unique need IDs from workflow requests
      const needIds = [...new Set(workflowRequests
        .map(req => req.payload?.needId)
        .filter(Boolean))]
        // ✅ FIX: Filter out invalid need IDs
        .filter(needId => {
          // Check if need ID is valid (not null, undefined, or empty)
          if (!needId || typeof needId !== 'string' || needId.trim() === '') {
            console.warn('⚠️ Skipping invalid need ID:', needId);
            return false;
          }
          
          // Check if ID looks like a valid UUID or database ID (not truncated)
          if (needId.includes('…') || needId.length < 8) {
            console.warn('⚠️ Skipping truncated/invalid need ID:', needId);
            return false;
          }
          
          return true;
        });
      
      // Load candidates for each valid need (if not already cached)
      // This loop is now redundant as getCandidatesForNeed is a store function
      // and needs are loaded via the store's needs array.
      // We keep it for now to ensure consistency with the original logic, but it might be removed later.
      needIds.forEach(needId => {
        const candidates = getCandidatesForNeed(needId);
        if (candidates.length === 0) {
          // Load candidates asynchronously if not cached
          // This was causing individual API calls. We'll rely on the store's getCandidatesForNeed.
          // loadCandidatesForNeed(needId)
          //   .catch(error => {
          //     // ✅ FIX: Handle 404 errors gracefully
          //     if (error.message && error.message.includes('Need not found')) {
          //       console.info(`Need ${needId} not found, skipping candidate load`);
          //     } else {
          //       console.error(`Error loading candidates for need ${needId}:`, error);
          //     }
          //   });
        }
      });
    }
  }, [workflowRequests, getCandidatesForNeed, loadNeeds]); // Removed loadCandidatesForNeed from dependencies

  // ✅ FIX: Helper function to extract business context from payload and lookup data
  const getBusinessContext = (request) => {
    const payload = request.payload || {};
    
    // Extract Need title and client by looking up in the needs array
    let needTitle = 'Unknown Need';
    let clientName = 'Unknown Client';
    
    if (payload.needId) {
      // ✅ FIX: Validate need ID before processing
      if (typeof payload.needId === 'string' && payload.needId.trim() !== '' && !payload.needId.includes('…')) {
        const need = needs.find(n => n.id === payload.needId);
        if (need) {
          needTitle = need.title || 'Untitled Need';
          clientName = need.client || 'Unknown Client';
        } else {
          // Fallback to payload data if need not found in store
          needTitle = payload.needTitle || payload.title || `Need ${payload.needId.substring(0, 8)}... (Not Found)`;
          clientName = payload.clientName || payload.client || payload.company || 'Unknown Client';
        }
      } else {
        // Invalid need ID
        needTitle = 'Invalid Need ID';
        clientName = 'Unknown Client';
      }
    }
    
    // Extract Candidate names - handle multiple candidates
    let candidateNames = [];
    
    // ✅ FIX: Only process candidates if we have a valid need ID
    if (payload.needId && typeof payload.needId === 'string' && payload.needId.trim() !== '' && !payload.needId.includes('…')) {
      if (payload.candidateId) {
        // Single candidate from payload - look up the specific candidate
        try {
          const candidatesForNeed = getCandidatesForNeed(payload.needId);
          const specificCandidate = candidatesForNeed.find(c => c.id === payload.candidateId);
          
          if (specificCandidate && specificCandidate.name) {
            candidateNames = [specificCandidate.name];
          } else {
            // Fallback to payload data for single candidate
            const candidateName = payload.candidateName || payload.candidate || 
                                 `Candidate ${payload.candidateId.substring(0, 8)}... (Not Found)`;
            candidateNames = [candidateName];
          }
        } catch (error) {
          // ✅ FIX: Handle errors gracefully
          console.warn(`Error getting candidate for need ${payload.needId}:`, error);
          const candidateName = payload.candidateName || payload.candidate || 'Unknown Candidate';
          candidateNames = [candidateName];
        }
      } else {
        // No specific candidate in payload, get all candidates for the need
        try {
          const candidatesForNeed = getCandidatesForNeed(payload.needId);
          if (candidatesForNeed && candidatesForNeed.length > 0) {
            candidateNames = candidatesForNeed.map(c => {
              if (c.name) return c.name;
              return c.id ? `Candidate ${c.id.substring(0, 8)}...` : 'Unknown Candidate';
            });
          } else {
            // No candidates found in store - show dead data
            candidateNames = ['No candidates found'];
          }
        } catch (error) {
          // ✅ FIX: Handle errors gracefully
          console.warn(`Error getting candidates for need ${payload.needId}:`, error);
          candidateNames = ['Error loading candidates'];
        }
      }
    }
    
    // Join multiple candidate names
    const candidateDisplay = candidateNames.length > 0 ? candidateNames.join(', ') : 'No candidates';
    
    // Extract Step label from payload
    const stepLabel = payload.stepType ? 
                     payload.stepType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) :
                     request.type;
    
    return { needTitle, clientName, candidateDisplay, stepLabel };
  };

  // ✅ NEW: Enhanced business context display with better dead data indicators
  const renderBusinessContextRow = (request) => {
    const context = getBusinessContext(request);
    
    return (
      <>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          <div className="flex items-center max-w-xs" title={context.needTitle || '—'}>
            <Briefcase className="w-4 h-4 text-indigo-500 mr-1" />
            <span className="truncate">
              {context.needTitle}
              {!needs.find(n => n.id === request.payload?.needId) && (
                <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                  Not Found
                </span>
              )}
            </span>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          <div className="flex items-center max-w-xs" title={context.clientName || '—'}>
            <Building2 className="w-4 h-4 text-indigo-500 mr-1" />
            <span className="truncate">{context.clientName}</span>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          <div className="flex items-center max-w-xs" title={context.candidateDisplay || '—'}>
            <User2 className="w-4 h-4 text-indigo-500 mr-1" />
            <span className="truncate">
              {context.candidateDisplay}
              {request.payload?.needId && getCandidatesForNeed(request.payload.needId).length === 0 && (
                <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                  No Data
                </span>
              )}
            </span>
          </div>
        </td>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ✅ MODIFY: Disable search bar in AppHeader */}
      <AppHeader showSearch={false} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Workflow Requests</h1>
            <p className="mt-2 text-sm text-gray-700">
              Manage and monitor RPA workflow requests in the system
            </p>
            <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
              <span className={`flex items-center ${isConnected ? 'text-green-600' : 'text-red-600'}`}>
                <div className={`w-2 h-2 rounded-full mr-1 ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
              <span>Last refresh: {getRelativeTime(lastRefresh)}</span>
              <span>{workflowRequests.length} requests</span>
            </div>
          </div>
          
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex sm:space-x-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="auto-refresh"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="auto-refresh" className="ml-2 text-sm text-gray-700">
                Auto-refresh
              </label>
            </div>
            
            <button
              onClick={() => setShowCreateDialog(true)}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              <Plus className="-ml-1 mr-2 h-4 w-4" />
              New Workflow
            </button>

            <button
              onClick={handleRefresh}
              disabled={workflowLoading}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`-ml-1 mr-2 h-4 w-4 ${workflowLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {workflowError && (
          <div className="mt-6 rounded-md bg-red-50 p-4">
            <div className="flex">
              <XCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading workflow requests</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{workflowError}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Need
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Candidate
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Step
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Progress
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created At
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Error
                      </th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quick Actions
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {workflowLoading && workflowRequests.length === 0 ? (
                      <tr>
                        <td colSpan="12" className="px-6 py-12 text-center">
                          <RefreshCw className="mx-auto h-8 w-8 text-gray-400 animate-spin" />
                          <p className="mt-2 text-sm text-gray-500">Loading workflow requests...</p>
                        </td>
                      </tr>
                    ) : workflowRequests.length === 0 ? (
                      <tr>
                        <td colSpan="12" className="px-6 py-12 text-center">
                          <p className="text-sm text-gray-500">No workflow requests found</p>
                          <p className="text-xs text-gray-400 mt-1">
                            Workflow requests are created automatically when you create interview or go/no-go steps
                          </p>
                        </td>
                      </tr>
                    ) : (
                      workflowRequests.map((request) => (
                        <tr key={request.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                            <div className="flex items-center">
                              <span className="text-gray-400">#</span>
                              {request.id.substring(0, 8)}
                            </div>
                          </td>
                          {/* ✅ USE: renderBusinessContextRow for consistent business context display */}
                          {renderBusinessContextRow(request)}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center max-w-xs" title={getBusinessContext(request).stepLabel || '—'}>
                              <ListChecks className="w-4 h-4 text-indigo-500 mr-1" />
                              <span className="truncate">{getBusinessContext(request).stepLabel}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {request.type}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {getStatusIcon(request.status)}
                              <span className={`ml-2 ${getStatusBadge(request.status)}`}>
                                {request.status}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center">
                              <span className="text-xs text-gray-500 mr-2">
                                {request.attempts} attempt{request.attempts !== 1 ? 's' : ''}
                              </span>
                              {request.status === 'IN_PROGRESS' && (
                                <div className="w-16 bg-gray-200 rounded-full h-1.5">
                                  <div className="bg-blue-600 h-1.5 rounded-full animate-pulse" style={{width: '60%'}}></div>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div>
                              {formatDate(request.createdAt)}
                            </div>
                            {request.updatedAt && request.updatedAt !== request.createdAt && (
                              <div className="text-xs text-gray-400">
                                Updated: {formatDate(request.updatedAt)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                            {request.lastError ? (
                              <div className="truncate text-red-600" title={request.lastError}>
                                {request.lastError}
                              </div>
                            ) : (
                              <span className="text-gray-400">No errors</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {canTrigger(request) || request.status === 'PENDING' ? (
                              <button
                                onClick={() => {
                                  // ✅ NEW: Check if this request needs payload construction
                                  if (needsPayloadConstruction(request)) {
                                    setSelectedRequest(request);
                                    setShowPayloadModal(true);
                                    return;
                                  }
                                  
                                  // Check for opportunity creation validation
                                  if (request.type === 'salesforce_opportunity_create' && request.payload?.needId) {
                                    const missingData = validateOpportunityCreation(request.payload.needId);
                                    if (missingData.length > 0) {
                                      const need = needs.find(n => n.id === request.payload.needId);
                                      setSelectedNeedForEdit(need);
                                      setShowEditModal(true);
                                      return;
                                    }
                                  }
                                  handleTrigger(request.id);
                                }}
                                disabled={localLoading[`trigger-${request.id}`]}
                                className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                                  // ✅ NEW: Show different color if validation required
                                  needsPayloadConstruction(request)
                                    ? 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500'
                                    : request.type === 'salesforce_opportunity_create' && request.payload?.needId && validateOpportunityCreation(request.payload.needId).length > 0
                                      ? 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500'
                                      : request.status === 'PENDING' 
                                        ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500' 
                                        : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                                }`}
                                title={
                                  request.type === 'salesforce_opportunity_create' && request.payload?.needId && validateOpportunityCreation(request.payload.needId).length > 0
                                    ? `Missing required data: ${validateOpportunityCreation(request.payload.needId).join(', ')}`
                                    : request.status === 'PENDING' 
                                      ? "Execute Pending Process" 
                                      : needsPayloadConstruction(request)
                                        ? "Configure and Start RPA Process"
                                        : "Start RPA Process"
                                }
                              >
                                {localLoading[`trigger-${request.id}`] ? (
                                  <>
                                    <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                    {request.status === 'PENDING' ? 'Executing...' : 'Starting...'}
                                  </>
                                ) : (
                                  <>
                                    {needsPayloadConstruction(request) ? (
                                      <>
                                        <Edit3 className="w-3 h-3 mr-1" />
                                        Configure
                                      </>
                                    ) : request.type === 'salesforce_opportunity_create' && request.payload?.needId && validateOpportunityCreation(request.payload.needId).length > 0 ? (
                                      <>
                                        <Edit3 className="w-3 h-3 mr-1" />
                                        Add Data
                                      </>
                                    ) : (
                                      <>
                                        <PlayCircle className="w-3 h-3 mr-1" />
                                        {request.status === 'PENDING' ? 'Execute' : 'Start'}
                                      </>
                                    )}
                                  </>
                                )}
                              </button>
                            ) : request.status === 'IN_PROGRESS' ? (
                              <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-md">
                                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                {request.flowId ? 'Processing...' : 'Initializing...'}
                              </span>
                            ) : request.status === 'COMPLETED' ? (
                              <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 rounded-md">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Completed
                              </span>
                            ) : request.status === 'FAILED' ? (
                              <button
                                onClick={() => handleRetry(request.id)}
                                disabled={localLoading[`retry-${request.id}`]}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Retry Failed Process"
                              >
                                {localLoading[`retry-${request.id}`] ? (
                                  <>
                                    <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                    Retrying...
                                  </>
                                ) : (
                                  <>
                                    <RotateCcw className="w-3 h-3 mr-1" />
                                    Retry
                                  </>
                                )}
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">No action</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-1">
                              <Link
                                to={`/admin/workflows/${request.id}`}
                                className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </Link>
                              
                              {canCancel(request) && (
                                <button
                                  onClick={() => handleCancel(request.id)}
                                  disabled={localLoading[`cancel-${request.id}`]}
                                  className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Cancel Workflow"
                                >
                                  {localLoading[`cancel-${request.id}`] ? (
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <StopCircle className="h-4 w-4" />
                                  )}
                                </button>
                              )}
                              
                              {canDelete(request) && (
                                <button
                                  onClick={() => handleDelete(request.id)}
                                  disabled={localLoading[`delete-${request.id}`]}
                                  className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Delete Workflow"
                                >
                                  {localLoading[`delete-${request.id}`] ? (
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ UPDATED: Edit Need Modal */}
      {showEditModal && selectedNeedForEdit && (
        <EditNeedModal
          need={selectedNeedForEdit}
          open={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedNeedForEdit(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedNeedForEdit(null);
            handleRefresh(); // Refresh the workflow requests
            loadNeeds(); // ✅ ADD: Refresh the needs data so validation sees the updated data
          }}
        />
      )}

      {/* ✅ NEW: Payload Construction Modal */}
      {showPayloadModal && selectedRequest && (
        <PayloadConstructionModal
          request={selectedRequest}
          isOpen={showPayloadModal}
          onClose={() => {
            setShowPayloadModal(false);
            setSelectedRequest(null);
          }}
          onSubmit={handlePayloadSubmit}
        />
      )}

      {/* Create Workflow Dialog */}
      <CreateWorkflowDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={handleRefresh}
      />

      <ConfirmDialog
        open={confirmAction.open}
        title={confirmAction.type === 'delete' ? 'Delete Workflow Request' : 'Cancel Workflow Request'}
        message={confirmAction.type === 'delete'
          ? 'Are you sure you want to delete this workflow request? This action cannot be undone.'
          : 'Are you sure you want to cancel this workflow request?'}
        confirmLabel={confirmAction.type === 'delete' ? 'Delete' : 'Cancel Request'}
        variant="danger"
        onConfirm={() => {
          if (confirmAction.type === 'delete') executeDelete(confirmAction.id);
          else executeCancel(confirmAction.id);
        }}
        onCancel={() => setConfirmAction({ open: false, type: null, id: null })}
      />
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default WorkflowRequestsList;
